import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { createPageUrl } from '@/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import HeroMedia from './HeroMedia';
import { useLanguage } from '@/i18n/LanguageProvider';

const initialHeroSlide = {
  id: 'initial-hero',
  position: 1,
  media_type: 'image',
  image: 'https://base44.app/api/apps/696b8a6ae79eec44c999be35/files/mp/public/696b8a6ae79eec44c999be35/f2eafcf5a_ChatGPTImage29lug202617_54_18AA.jpg',
  image_meta: { title: 'Casa Sicily', description: 'Architettura residenziale contemporanea firmata Bellomonte & Pensabene' }
};

export default function HeroSection() {
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [viewingVideo, setViewingVideo] = useState(false);
  const { data: slides = [] } = useQuery({
    queryKey: ['home-slides'],
    queryFn: () => base44.entities.HomeSlide.list('position', 3),
    placeholderData: [initialHeroSlide],
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (viewingVideo || slides.length < 2) return;
    const timer = setInterval(() => setCurrentIndex((current) => (current + 1) % slides.length), 6000);
    return () => clearInterval(timer);
  }, [slides.length, viewingVideo]);

  if (!slides.length) return <section className="relative h-[82svh] w-full bg-[#2d2d2d] md:h-screen" />;
  const selectSlide = (index) => { setViewingVideo(false); setCurrentIndex(index); };

  return <section className="relative h-[82svh] w-full overflow-hidden md:h-screen">
    <h1 className="sr-only">Bellomonte & Pensabene — Studio di Architettura e Interior Design</h1>
    <AnimatePresence mode="wait" initial={false}>
      <motion.div key={currentIndex} initial={{ opacity: 0, scale: isMobile ? 1 : 1.05 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: isMobile ? 0.35 : 1.2, ease: 'easeInOut' }} className="absolute inset-0">
        <HeroMedia slide={slides[currentIndex]} viewing={viewingVideo} onView={() => setViewingVideo(true)} />
        <div className="pointer-events-none absolute inset-0 bg-black/20" />
      </motion.div>
    </AnimatePresence>
    <Link to={createPageUrl('Projects')} className="absolute bottom-16 left-6 z-20 inline-flex min-h-12 items-center border border-white/80 bg-black/20 px-6 text-sm uppercase tracking-[0.2em] text-white backdrop-blur-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white md:hidden">{t('Scopri i progetti')}</Link>
    <div className="absolute bottom-5 right-6 z-20 flex gap-2 md:bottom-24 md:left-1/2 md:right-auto md:-translate-x-1/2 md:gap-3">
      {slides.map((slide, index) => <button key={slide.id || index} onClick={() => selectSlide(index)} aria-label={`${t('Mostra contenuto')} ${index + 1}`} className={`flex h-11 w-8 items-center md:h-4 md:w-12 ${index === currentIndex ? 'opacity-100' : 'opacity-50'}`}><span className="h-px w-full bg-white" /></button>)}
    </div>
    <motion.button onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })} initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.3 }} aria-label={t('Scorri alla sezione successiva')} className="absolute bottom-8 left-1/2 z-20 hidden min-h-11 min-w-11 -translate-x-1/2 flex-col items-center justify-center text-white hover:opacity-70 md:flex">
      <ChevronDown size={24} strokeWidth={1} />
    </motion.button>
  </section>;
}